import { Box, Grid, Stack, useMediaQuery, useTheme } from '@mui/material';
import {
    createContext,
    ReactElement,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import CompanyDetails from './component/CompanyDetails';
import SolicitorDetails from './component/SolicitorDetails';
import ClientSettings from './component/ClientSettings';
import { ButtonComp } from 'shared/components/AppComponents';
import AddClientDialog from './component/AddClientDialog';
import RemoveClientDialog from './component/RemoveClientDialog';
import ReplaceClientDialog from './component/ReplaceClientDialog';
import { UploadService, ClientService } from 'services';
import { SolicitorData } from 'modules/authentication/components/ManageClientsPage';
import { AzureStorage } from 'shared/helpers/AzureStorage';
import { ChecklistStep, ClientCheckList, ClientData } from './Main';
import { Toast, Utils } from 'shared/helpers';
import {
    clientScheme,
    clientWithChecklistScheme,
} from 'validators/ClientScheme';
import { Formik, FormikProps } from 'formik';
import { ClientEnd } from 'shared/Constants';
import isEqual from 'lodash.isequal';
import { useLocation, useParams } from 'react-router-dom';
import { formattingAction } from 'shared/helpers/FormatAction';
import {
    useFormikEffect,
    useFormikElementOnValidationEffect,
} from 'shared/helpers/Formik';
import { useDispatch, useSelector } from 'react-redux';
import { setRouteReloaded } from 'state/persistapplication/Action';
import { AxiosError } from 'axios';

/**
 * MCActionContent component.
 * Renders the content for adding a new client.
 * @returns {ReactElement} MCActionContent component.
 */

export const ActionClientMeta = createContext<ClientMetaProps | null>(null);

const MCActionContent = ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    clientChecklist: clientChecklistProps,
    clientID: clientIDProps,
    clientEmail,
    clientName,
    clientPhone,
    statusPage,
    slaDays,
    cm9Days,
    reworkDays,
    archiveDays,

    clientSolicitorData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    clientChecklistStep: clientChecklistStepProps,
    setStatusPage,
}: MCActionContent): ReactElement => {
    const location = useLocation();
    const dispatch = useDispatch();
    const isReloaded = useSelector((state: any) => state.persistState.isReload);
    const isReload = useMemo(() => isReloaded, []);
    const { clientId } = useParams();
    const [clientID, setClientID] = useState<string>(clientId ?? clientIDProps);
    const innerButtonRef = useRef<HTMLButtonElement | null>(null);
    const [clientChecklist, setStatusClientChecklist] = useState<
        ClientCheckList[]
    >([]);
    const [clientChecklistStep, SetStatusClientChecklistStepData] = useState<
        ChecklistStep[]
    >([]);
    const formikRef = useRef<FormikProps<FormikPropsValues> | null>(null);
    const [initValues, setInitValues] = useState<FormikPropsValues | null>(
        null
    );
    const [isToSubmited, setSubmited] = useState<boolean>(false);
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.up('md'));
    const matchesMD = useMediaQuery(theme.breakpoints.only('md'));
    const matchesDownMD = useMediaQuery(theme.breakpoints.down('md'));
    const [isSubmitting, setSubmitting] = useState<boolean>(false);
    const [dialogAdd, setDialogAdd] = useState<boolean>(false);
    const [dialogReplace, setDialogReplace] = useState<boolean>(false);
    const [dialogRemove, setDialogRemove] = useState<boolean>(false);
    const fileRef = useRef<HTMLInputElement>(null);
    const [statusReplace, setStatusReplace] = useState<boolean>(false);
    const [fileChecklistName, setFileChecklistName] = useState<string>('');
    const [fileChecklistFilename, setFileChecklistFilename] =
        useState<string>('');

    /**
     * Handles the click event on the upload button.
     */
    const handleClick = (): void => {
        fileRef.current?.click();
    };
    const onUnload = useCallback(() => {
        dispatch(setRouteReloaded(true));
    }, [dispatch]);

    useEffect(() => {
        dispatch(setRouteReloaded(false));
        window.addEventListener('beforeunload', onUnload);
        return () => {
            window.removeEventListener('beforeunload', onUnload);
        };
    }, []);

    const handleChangesInputFile = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files && event.target.files?.length > 0) {
                const file: File = event.target.files[0];
                const checklistNameHelper =
                    formikRef.current?.getFieldHelpers('checklistName');
                const checklistNameField =
                    formikRef.current?.getFieldMeta('checklistName');
                const checklistHelper =
                    formikRef.current?.getFieldHelpers('checklist');

                if (
                    checklistNameField?.value !== '' &&
                    typeof checklistNameField?.value === 'string'
                ) {
                    setFileChecklistName(checklistNameField?.value);
                    setFileChecklistFilename(file.name);
                    if (checklistHelper) {
                        checklistHelper.setValue(file.name);
                    }
                } else {
                    setFileChecklistName(file.name.replace(/\.[^/.]+$/, ''));
                    setFileChecklistFilename(file.name);
                    if (checklistNameHelper && checklistHelper) {
                        checklistNameHelper.setValue(
                            file.name.replace(/\.[^/.]+$/, '')
                        );
                        checklistHelper.setValue(file.name);
                    }
                }
            }
        },
        []
    );
    const createFilesWithSignedAttr = (file: File[]): Promise<FileSigned[]> =>
        new Promise((resolve, reject) => {
            UploadService.signedUrl({
                fileNameList: Array.from(file).map((i) => i.name),
                clientId: null,
                binNumber: null,
                type: 'checklist',
            })
                .then((response) => {
                    resolve(
                        Array.from(response.data.data.uploadList).map(
                            (e: any) => ({
                                signedfilename: e.placeholder,
                                url: e.sasUrl,
                            })
                        )
                    );
                })
                .catch((error) => {
                    reject(error);
                });
        });

    const asyncUploadFilesTokenizationBasedURL = useCallback(
        (file: File | null, signed: FileSigned): Promise<void> =>
            new Promise(async (resolve, reject) => {
                if (file === null) {
                    reject();
                } else {
                    const uint8View = await file
                        .arrayBuffer()
                        .then((value) => new Uint8Array(value));
                    AzureStorage(
                        uint8View,
                        signed.url ?? '',
                        () => {},
                        () => {}
                    )
                        .then(resolve)
                        .catch(reject);
                }
            }),
        []
    );

    const getChecklistFile = useCallback(
        async (): Promise<{
            placeHolder: string;
            file: string;
        } | null> =>
            new Promise(async (resolve, reject) => {
                try {
                    const files: FileList | null | undefined =
                        fileRef.current?.files;
                    const checklistFile = {
                        placeHolder: '',
                        file: '',
                    };
                    if (files && files.length > 0) {
                        const fileNameList: File[] = Array.from(files)
                            .map((value) => value)
                            .filter((o, i) => i === 0);
                        // const fileSigned: Promise<FileSigned[]> = Promise.all(
                        //     Array.from(files).map((value) =>
                        //         createFilesWithSignedAttr(value)
                        //     )
                        // );
                        const fileSignedSettled: FileSigned[] | null =
                            await createFilesWithSignedAttr(fileNameList)
                                .then((value) => value)
                                .catch((error) => {
                                    throw error;
                                });

                        await Promise.all(
                            Array.from(fileSignedSettled ?? []).map(
                                (value, index) => {
                                    if (fileRef.current?.files)
                                        return asyncUploadFilesTokenizationBasedURL(
                                            fileRef.current?.files[index],
                                            value
                                        );

                                    return asyncUploadFilesTokenizationBasedURL(
                                        null,
                                        value
                                    );
                                }
                            )
                        )
                            .then(async () => {
                                checklistFile.placeHolder =
                                    fileChecklistName || files[0].name;
                                checklistFile.file =
                                    fileSignedSettled[0].signedfilename;
                                if (
                                    checklistFile.file &&
                                    checklistFile.placeHolder
                                ) {
                                    resolve(checklistFile);
                                }
                            })
                            .catch((error) => {
                                if (
                                    error &&
                                    error instanceof AxiosError &&
                                    error.response &&
                                    error.response.status === 400
                                ) {
                                    Toast.showErrorMessage(
                                        error.response.statusText
                                    );
                                }
                                throw error;
                            });
                    }
                } catch (error) {
                    reject(error);
                }
            }),
        [asyncUploadFilesTokenizationBasedURL, fileChecklistName]
    );

    const handleOnReplaceClientModalForFileCheckList = useCallback(
        async (values: FormikPropsValues) => {
            const {
                solicitorData,
                clientName: _clientName,
                clientEmail: _clientEmail,
                contactNumber,
                archiveDays: _archiveDays,
                SLADays,
                cm9Days: _archiveCM9Days,
                reworkDays: _archiveReworkDays,
            } = values;

            try {
                const CheckListFile = await getChecklistFile()
                    .then((data) => data)
                    .catch((error) => {
                        throw error;
                    });
                if (CheckListFile && CheckListFile.file) {
                    Promise.all([
                        ClientService.replaceClientChecklist({
                            clientId: clientID,
                            placeHolder: CheckListFile.placeHolder,
                            file: CheckListFile.file,
                        }),
                        ClientService.editClient({
                            clientId: clientID,
                            name: _clientName,
                            email: _clientEmail,
                            timeToCm9:
                                parseInt(_archiveCM9Days.toString(), 10) ?? 0,
                            timeToRework:
                                parseInt(_archiveReworkDays.toString(), 10) ??
                                0,
                            contactNumber: contactNumber.toString(),
                            removePacketInDays:
                                parseInt(_archiveDays.toString(), 10) ?? 0,
                            timeToSla: parseInt(SLADays.toString(), 10) ?? 0,
                            solicitorDetails: Array.from(solicitorData).map(
                                ({ companyName, contacts }) => ({
                                    companyName,
                                    contacts: Array.from(contacts)
                                        .map((el) => ({
                                            name: el.name,
                                            phone: el.phone
                                                ? el.phone
                                                : parseInt(
                                                      el.contactNumber ?? '',
                                                      10
                                                  ),
                                            email:
                                                typeof el.email === 'string'
                                                    ? el.email
                                                    : el.email.id,
                                        }))
                                        .filter(
                                            (o) =>
                                                o.email !== '' &&
                                                o.name !== '' &&
                                                o.phone !== null &&
                                                o.phone !== '' &&
                                                o.phone !== 0
                                        ),
                                })
                            ),
                        }),
                    ])
                        .then((response) => {
                            response.forEach((element) => {
                                if (
                                    element.config.url ===
                                    ClientEnd.REPLACE_CLIENT_CHECKLIST
                                )
                                    Utils.handleSuccessResponse(element);
                            });
                            setSubmitting(false);
                            setStatusPage('view');
                            setDialogReplace(false);
                        })
                        .catch((error) => {
                            setSubmitting(false);
                            if (
                                error !== null &&
                                error instanceof AxiosError &&
                                error.code === 'ERR_NETWORK'
                            ) {
                                Toast.showErrorMessage(
                                    'No internet connection, Please try again!'
                                );
                            } else {
                                Utils.handleErrorResponse(error);
                            }
                        });
                }
            } catch (e: any) {
                setSubmitting(false);
                if (
                    e !== null &&
                    e instanceof AxiosError &&
                    e.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(e);
                }
            }
        },
        [clientID, getChecklistFile, setStatusPage]
    );

    const getValuesIsChanged = useCallback(
        (values: FormikPropsValues): boolean => isEqual(values, initValues),
        [initValues]
    );

    const handleOnSaveButtonForEditingClient = useCallback(
        async (values: FormikPropsValues) => {
            const {
                solicitorData,
                clientName: _clientName,
                clientEmail: _clientEmail,
                contactNumber,
                archiveDays: _archiveDays,
                SLADays,
                cm9Days: _archiveCM9Days,
                reworkDays: _archiveReworkDays,
            } = values;

            const isNoChanging = getValuesIsChanged(values);

            if (isNoChanging) {
                setSubmitting(false);
                setStatusPage('view');
            } else {
                ClientService.editClient({
                    clientId: clientID,
                    name: _clientName,
                    email: _clientEmail,
                    timeToCm9: parseInt(_archiveCM9Days.toString(), 10) ?? 0,
                    timeToRework:
                        parseInt(_archiveReworkDays.toString(), 10) ?? 0,
                    contactNumber: contactNumber.toString(),
                    removePacketInDays:
                        parseInt(_archiveDays.toString(), 10) ?? 0,
                    timeToSla: parseInt(SLADays.toString(), 10) ?? 0,
                    solicitorDetails: Array.from(solicitorData).map(
                        ({ companyName, contacts }) => ({
                            companyName,
                            contacts: Array.from(contacts)
                                .map((el) => ({
                                    name: el.name,
                                    phone: el.phone
                                        ? el.phone
                                        : parseInt(el.contactNumber ?? '', 10),
                                    email:
                                        typeof el.email === 'string'
                                            ? el.email
                                            : el.email.id,
                                }))
                                .filter(
                                    (o) =>
                                        o.email !== '' &&
                                        o.name !== '' &&
                                        o.phone !== null &&
                                        o.phone !== '' &&
                                        o.phone !== 0
                                ),
                        })
                    ),
                })
                    .then((reponse) => {
                        Utils.handleSuccessResponse(reponse);
                        setSubmitting(false);
                        if (statusReplace) {
                            setDialogReplace(true);
                        } else {
                            setStatusPage('view');
                        }
                    })
                    .catch((error) => {
                        setSubmitting(false);
                        if (
                            error !== null &&
                            error instanceof AxiosError &&
                            error.code === 'ERR_NETWORK'
                        ) {
                            Toast.showErrorMessage(
                                'No internet connection, Please try again!'
                            );
                        } else {
                            Utils.handleErrorResponse(error);
                        }
                    });
            }
        },
        [clientID, getValuesIsChanged, setStatusPage, statusReplace]
    );

    const handleOnSaveButtonForAddingClient = useCallback(
        async (values: FormikPropsValues) => {
            const {
                solicitorData,
                clientName: _clientName,
                clientEmail: _clientEmail,
                contactNumber,
                archiveDays: _archiveDays,
                SLADays,
                cm9Days: _archiveCM9Days,
                reworkDays: _archiveReworkDays,
            } = values;
            try {
                const CheckListFile = await getChecklistFile()
                    .then((data) => data)

                    .catch((error: any) => {
                        throw error;
                    });
                if (CheckListFile) {
                    ClientService.addClient({
                        name: _clientName,
                        email: _clientEmail,
                        contactNumber: contactNumber.toString(),
                        timeToCm9:
                            parseInt(_archiveCM9Days.toString(), 10) ?? 0,
                        timeToRework:
                            parseInt(_archiveReworkDays.toString(), 10) ?? 0,
                        removePacketInDays:
                            parseInt(_archiveDays.toString(), 10) ?? 0,
                        timeToSla: parseInt(SLADays.toString(), 10) ?? 0,
                        solicitorDetails: Array.from(solicitorData).map(
                            ({ companyName, contacts }) => ({
                                companyName,
                                contacts: Array.from(contacts)
                                    .map((el) => ({
                                        name: el.name,
                                        phone: el.phone
                                            ? el.phone
                                            : parseInt(
                                                  el.contactNumber ?? '',
                                                  10
                                              ),
                                        email:
                                            typeof el.email === 'string'
                                                ? el.email
                                                : el.email.id,
                                    }))
                                    .filter(
                                        (o) =>
                                            o.email !== '' &&
                                            o.name !== '' &&
                                            o.phone !== null &&
                                            o.phone !== '' &&
                                            o.phone !== 0
                                    ),
                            })
                        ),
                        checkList: CheckListFile,
                    })
                        .then((reponse) => {
                            Utils.handleSuccessResponse(reponse);
                            setSubmitting(false);
                            setDialogAdd(true);
                        })
                        .catch((error) => {
                            setSubmitting(false);
                            if (
                                error !== null &&
                                error instanceof AxiosError &&
                                error.code === 'ERR_NETWORK'
                            ) {
                                Toast.showErrorMessage(
                                    'No internet connection, Please try again!'
                                );
                            } else {
                                Utils.handleErrorResponse(error);
                            }
                        });
                }
            } catch (e: any) {
                setSubmitting(false);
                if (
                    e !== null &&
                    e instanceof AxiosError &&
                    e.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(e);
                }
            }
        },
        [getChecklistFile]
    );

    const handleFormikValidationForSolicitorDetailsOnAddCompanyButton =
        useCallback((solicitorData: SolicitorData[]) => {
            const solicitorCompanyFieldMeta =
                formikRef.current?.getFieldMeta('solicitorCompany');
            const validate = formikRef.current
                ?.validateForm()
                .then((response) => {
                    if (
                        response &&
                        Object.keys(response).includes('solicitorCompany') &&
                        response.solicitorCompany !==
                            'Reviewer company name need to be added before submit'
                    ) {
                        formikRef.current?.setFieldTouched(
                            'solicitorCompany',
                            true
                        );
                        return false;
                    }

                    if (solicitorData.length > 0) {
                        if (
                            typeof solicitorCompanyFieldMeta?.value ===
                                'string' &&
                            solicitorCompanyFieldMeta?.value === ''
                        ) {
                            formikRef.current?.setFieldTouched(
                                'solicitorCompany',
                                true,
                                false
                            );
                            formikRef.current?.setFieldError(
                                'solicitorCompany',
                                'Reviewer company name need to be added'
                            );

                            return false;
                        }
                        if (
                            typeof solicitorCompanyFieldMeta?.value ===
                                'string' &&
                            solicitorCompanyFieldMeta?.value.length > 64
                        ) {
                            formikRef.current?.setFieldTouched(
                                'solicitorCompany',
                                true,
                                false
                            );
                            formikRef.current?.setFieldError(
                                'solicitorCompany',
                                'Reviewer company name have limitation of 64 characters'
                            );

                            return false;
                        }
                    }

                    return true;
                })
                .catch(() => false);

            return validate;
        }, []);

    const handleFormikValidationForSolicitorDetailsOnAddSolicitorButton =
        useCallback((index: number, dataIndex: number) => {
            const key = `solicitorData[${index}].contacts[${dataIndex}]`;
            const keyName = `${key}.name`;
            const keyPhone = `${key}.email`;
            const keyEmail = `${key}.phone`;
            const validate = formikRef.current
                ?.validateForm()
                .then((response) => {
                    if (
                        response &&
                        Object.keys(response).includes('solicitorData') &&
                        Array.isArray(response.solicitorData) &&
                        response.solicitorData[index] &&
                        Object.keys(response.solicitorData[index]).includes(
                            'contacts'
                        )
                    ) {
                        const solicitorDataValidation =
                            response.solicitorData[index];
                        if (
                            typeof solicitorDataValidation !== 'string' &&
                            Array.isArray(solicitorDataValidation.contacts) &&
                            solicitorDataValidation.contacts.some(
                                (o, i) =>
                                    typeof o !== 'string' && i === dataIndex
                            )
                        ) {
                            formikRef.current?.setFieldTouched(keyName, true);
                            formikRef.current?.setFieldTouched(keyPhone, true);
                            formikRef.current?.setFieldTouched(keyEmail, true);
                            return false;
                        }

                        return true;
                    }
                    return true;
                })
                .catch(() => false);

            return validate;
        }, []);

    useLayoutEffect(() => {
        const contactNumberHelper =
            formikRef.current?.getFieldHelpers('contactNumber');
        const SLADaysHelper = formikRef.current?.getFieldHelpers('SLADays');
        const cm9DaysHelper = formikRef.current?.getFieldHelpers('cm9Days');
        const reworkDaysHelper =
            formikRef.current?.getFieldHelpers('reworkDays');
        const archiveDaysHelper =
            formikRef.current?.getFieldHelpers('archiveDays');
        const solicitorDataHelper =
            formikRef.current?.getFieldHelpers('solicitorData');
        const clientNameHelper =
            formikRef.current?.getFieldHelpers('clientName');
        const clientEmailHelper =
            formikRef.current?.getFieldHelpers('clientEmail');
        const formikValues = formikRef.current?.values;
        if (
            statusPage === 'edit' &&
            contactNumberHelper &&
            SLADaysHelper &&
            cm9DaysHelper &&
            reworkDaysHelper &&
            archiveDaysHelper &&
            solicitorDataHelper &&
            clientNameHelper &&
            clientEmailHelper
        ) {
            if (
                isReload === false &&
                location.state &&
                Object.keys(location.state as any).length > 0
            ) {
                const {
                    clientPhone: clientPhoneState,
                    clientName: clientNameState,
                    clientEmail: clientEmailState,
                    slaDays: slaDaysState,
                    cm9Days: cm9DaysState,
                    reworkDays: reworkDaysState,
                    archiveDays: archiveDaysState,
                    clientSolicitorData: clientSolicitorDataState,
                    clientChecklist: clientChecklistState,
                    clientChecklistStep: clientChecklistStepState,
                } = location.state as any;
                contactNumberHelper.setValue(clientPhoneState);
                SLADaysHelper.setValue(slaDaysState);
                cm9DaysHelper.setValue(cm9DaysState);
                reworkDaysHelper.setValue(reworkDaysState);
                archiveDaysHelper.setValue(archiveDaysState);
                solicitorDataHelper.setValue(clientSolicitorDataState);

                clientNameHelper.setValue(clientNameState);
                clientEmailHelper.setValue(clientEmailState);
                setInitValues({
                    solicitorCompany: formikValues?.solicitorCompany ?? '',
                    archiveDays,
                    SLADays: slaDaysState,
                    checklist: '',
                    checklistName: '',
                    solicitorData: clientSolicitorDataState ?? [],
                    clientName: clientNameState ?? '',
                    clientEmail: clientEmailState ?? '',
                    contactNumber: clientPhoneState,
                    cm9Days: cm9DaysState,
                    reworkDays: reworkDaysState,
                });
                setStatusClientChecklist(clientChecklistState);
                SetStatusClientChecklistStepData(clientChecklistStepState);
            } else {
                ClientService.getClient({
                    clientId: clientId ?? '',
                })
                    .then(
                        (response: {
                            data: {
                                msg: string;
                                data: ClientData;
                            };
                        }) => {
                            contactNumberHelper.setValue(
                                response.data.data.contactNumber
                            );
                            cm9DaysHelper.setValue(
                                response.data.data.timeToCm9
                            );
                            reworkDaysHelper.setValue(
                                response.data.data.timeToRework
                            );
                            SLADaysHelper.setValue(
                                response.data.data.timeToSla
                            );
                            archiveDaysHelper.setValue(
                                response.data.data.removePacketInDays
                            );
                            const solicitorCompanyData = Array.from(
                                response.data.data.solicitors
                            ).map((el) => ({
                                ...el,
                                contacts:
                                    Array.isArray(el.contacts) &&
                                    el.contacts.length > 0
                                        ? Array.from(el.contacts).map(
                                              (contact) => ({
                                                  name: contact.name,
                                                  email:
                                                      typeof contact.email !==
                                                      'string'
                                                          ? contact.email.id
                                                          : contact.email,
                                                  phone: contact.phone,
                                              })
                                          )
                                        : [
                                              {
                                                  name: '',
                                                  email: '',
                                                  phone: '',
                                              },
                                          ],
                            }));

                            solicitorDataHelper.setValue(solicitorCompanyData);

                            clientNameHelper.setValue(response.data.data.name);
                            clientEmailHelper.setValue(
                                response.data.data.email
                            );
                            setInitValues({
                                solicitorCompany:
                                    formikValues?.solicitorCompany ?? '',
                                archiveDays,
                                SLADays: response.data.data.timeToSla,
                                checklist: '',
                                checklistName: '',
                                solicitorData: solicitorCompanyData,
                                clientName: response.data.data.name ?? '',
                                clientEmail: response.data.data.email ?? '',
                                contactNumber: response.data.data.contactNumber,
                                cm9Days: response.data.data.timeToCm9,
                                reworkDays: response.data.data.timeToRework,
                            });

                            setStatusClientChecklist(
                                response.data.data.checkList
                            );
                            SetStatusClientChecklistStepData(
                                Array.from(response.data.data.checkListSteps)
                                    .sort((a, b) => a.step - b.step)
                                    .map((el) => ({
                                        ...el,
                                        actions: formattingAction(el),
                                    }))
                            );
                            const dataUUID = formattingAction(
                                response.data.data
                            );
                            setClientID(dataUUID.uuid);
                        }
                    )
                    .catch((error) => {
                        Utils.handleErrorResponse(error);
                    });
                contactNumberHelper.setValue(clientPhone);
                SLADaysHelper.setValue(slaDays);
                archiveDaysHelper.setValue(archiveDays);
                cm9DaysHelper.setValue(cm9Days);
                reworkDaysHelper.setValue(reworkDays);
                setInitValues({
                    solicitorCompany: formikValues?.solicitorCompany ?? '',
                    archiveDays,
                    SLADays: slaDays,
                    checklist: '',
                    checklistName: '',
                    solicitorData: formikValues?.solicitorData ?? [],
                    clientName: formikValues?.clientName ?? '',
                    clientEmail: formikValues?.clientEmail ?? '',
                    contactNumber: clientPhone,
                    cm9Days,
                    reworkDays,
                });
            }
        }
    }, [statusPage]);

    const getValidationSchemeBasedOnStatusPage = useCallback(() => {
        if (statusPage === 'edit') {
            if (statusReplace) {
                return clientWithChecklistScheme;
            }
            return clientScheme;
        }
        return clientWithChecklistScheme;
    }, [statusPage, statusReplace]);

    const currentChecklist = useMemo(() => {
        if (
            Array.isArray(clientChecklist) &&
            clientChecklist.length > 0 &&
            clientChecklist.some((o) => o.enabled === true)
        ) {
            return clientChecklist.find((o) => o.enabled === true);
        }
        return clientChecklist.at(0);
    }, [clientChecklist]);

    const [
        isUpdateSolicitorFormFormikValue,
        setUpdateSolicitorFormFormikValue,
    ] = useState<boolean>(false);
    const [isSolicitorDetailUpdated, setSolicitorDetailUpdated] =
        useState<boolean>(false);
    const handleFormikDetailUpdated = useCallback(() => {
        setSolicitorDetailUpdated(true);
        setUpdateSolicitorFormFormikValue(true);
    }, []);

    useLayoutEffect(() => {
        if (isSolicitorDetailUpdated) {
            setSolicitorDetailUpdated(false);
        }
    }, [isSolicitorDetailUpdated]);

    useFormikEffect(
        (value: any) => {
            const { current: formik } = formikRef;
            if (formik && value && Object.keys(value).length > 0) {
                formik.setValues(
                    {
                        solicitorCompany: value.solicitorCompany,
                        archiveDays: value.archiveDays,
                        SLADays: value.SLADays,
                        checklist: value.checklist,
                        checklistName: value.checklistName,
                        solicitorData: value.solicitorData,
                        clientName: value.clientName,
                        clientEmail: value.clientEmail,
                        contactNumber: value.contactNumber,
                        cm9Days: value.cm9Days,
                        reworkDays: value.reworkDays,
                    },
                    true
                );
                // console.log({ ...value });
            }
            // formikRef.current?.setValues(value);
        },
        [
            'clientName',
            'solicitorCompany',
            'archiveDays',
            'SLADays',
            'cm9Days',
            'reworkDays',
            'solicitorData',
            'clientEmail',
            'contactNumber',
        ],
        '/manage-checklists/add',
        true,
        500,
        formikRef
    );

    const handleClientDeletation = useCallback(() => {
        ClientService.deleteClient({ clientId: clientID })
            .then((res) => {
                Utils.handleSuccessResponse(res);
                setStatusPage('view');
            })
            .catch(Utils.handleErrorResponse);
    }, [clientID, setStatusPage]);

    const fieldListSolicitorDetail = useMemo(() => {
        const formikValues = formikRef.current?.values.solicitorData ?? [];
        const values = isUpdateSolicitorFormFormikValue
            ? formikValues
            : clientSolicitorData;
        const primer = Array.from(values).map((e, d) =>
            Array.from({ length: e.contacts.length }).map((x, i) => [
                `solicitorData[${d}].contacts[${i}].name`,
                `solicitorData[${d}].contacts[${i}].email`,
                `solicitorData[${d}].contacts[${i}].phone`,
            ])
        );

        return primer.flat(3);
    }, [
        clientSolicitorData,
        isUpdateSolicitorFormFormikValue,
        isSolicitorDetailUpdated,
    ]);

    const fieldListOnClientForm = useMemo(
        () =>
            [
                'clientName',
                'clientEmail',
                'contactNumber',
                'solicitorCompany',
                'archiveDays',
                'SLADays',
                'reworkDays',
                'cm9Days',
                'checklist',
                'checklistName',
            ].concat(fieldListSolicitorDetail),
        [fieldListSolicitorDetail]
    );

    const handleSubmitOnManageClient = useCallback(() => {
        const { current: formik } = formikRef;
        if (formik) {
            setSubmited(true);
            setTimeout(() => formik.handleSubmit(), 200);
        }
    }, []);
    const handleFormikToSubmitUsingElementScrollView = useCallback(
        (el: Element | null) => {
            if (isToSubmited && el && typeof el.scrollIntoView === 'function') {
                el?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
                setSubmited(false);
            }
        },
        [isToSubmited]
    );
    useFormikElementOnValidationEffect(
        (val, element) => {
            if (val === false) {
                handleFormikToSubmitUsingElementScrollView(element);
            }
        },
        fieldListOnClientForm,
        formikRef
    );

    const isHavingFile = useMemo(() => {
        const { current: fileContainer } = fileRef;
        if (
            fileContainer &&
            fileContainer.files &&
            fileContainer.files.length > 0
        ) {
            return true;
        }
        return false;
    }, [statusReplace]);

    const isHavingValidFile = useMemo(() => {
        const { current: fileContainer } = fileRef;
        if (
            fileContainer &&
            fileContainer.files &&
            fileContainer.files.length > 0 &&
            Object.values(fileContainer.files).some(
                (e) => /^.*\.(PDF|pdf)$/.test(e.name) === false
            ) !== true &&
            fileChecklistName.length > 0
        ) {
            return true;
        }
        return false;
    }, [statusReplace]);

    const [isSaveButtonFocus, setSaveButtonFocus] = useState<boolean>(false);
    const initialActionClientMeta = useMemo(
        () => ({
            isSaveButtonFocus,
            setSaveButtonFocus,
        }),
        [isSaveButtonFocus]
    );
    const handleMouseOverSavingButton = useCallback(() => {
        setSaveButtonFocus(true);
    }, []);
    const handleMouseLeaveSavingButton = useCallback(() => {
        setSaveButtonFocus(false);
    }, []);
    useLayoutEffect(() => {
        const { current: buttonRef } = innerButtonRef;
        if (buttonRef) {
            buttonRef.onmouseover = handleMouseOverSavingButton;
            buttonRef.onmouseleave = handleMouseLeaveSavingButton;
        }
    }, [statusPage]);

    return (
        <Formik
            innerRef={formikRef}
            initialValues={{
                solicitorCompany: '',
                archiveDays: '',
                SLADays: '',
                cm9Days: '',
                reworkDays: '',
                checklist: '',
                checklistName: '',
                solicitorData: clientSolicitorData,
                clientName: '',
                clientEmail: '',
                contactNumber: '',
            }}
            enableReinitialize
            validationSchema={getValidationSchemeBasedOnStatusPage}
            onSubmit={(values) => {
                setSubmited(false);
                if (statusPage === 'add') {
                    setSubmitting(true);
                    handleOnSaveButtonForAddingClient(values);
                } else if (
                    statusPage === 'edit' &&
                    !statusReplace &&
                    isHavingFile === false
                ) {
                    setSubmitting(true);
                    handleOnSaveButtonForEditingClient(values);
                } else if (
                    statusPage === 'edit' &&
                    (statusReplace || isHavingFile === true) &&
                    !dialogReplace
                ) {
                    if (
                        (statusReplace === false &&
                            isHavingFile === true &&
                            isHavingValidFile === true) ||
                        statusReplace
                    ) {
                        setDialogReplace(true);
                    } else {
                        Toast.showErrorMessage(
                            'Checklist name is required or checklist file type need to be pdf'
                        );
                    }
                } else if (
                    statusPage === 'edit' &&
                    (statusReplace || isHavingFile === true) &&
                    dialogReplace
                ) {
                    setSubmitting(true);
                    handleOnReplaceClientModalForFileCheckList(values);
                }
            }}
        >
            {() => (
                <Box className="WrapperActionClient">
                    <ActionClientMeta.Provider value={initialActionClientMeta}>
                        <AddClientDialog
                            dialogAdd={dialogAdd}
                            setDialogAdd={setDialogAdd}
                            setStatusPage={setStatusPage}
                        />

                        <RemoveClientDialog
                            dialogRemove={dialogRemove}
                            setDialogRemove={setDialogRemove}
                            setStatusPage={handleClientDeletation}
                        />

                        <ReplaceClientDialog
                            handleDialogSubmit={handleSubmitOnManageClient}
                            dialogReplace={dialogReplace}
                            isLoading={isSubmitting}
                            setDialogReplace={setDialogReplace}
                        />

                        <input
                            type="file"
                            hidden
                            ref={fileRef}
                            accept=".zip,application/pdf"
                            multiple={false}
                            onChange={handleChangesInputFile}
                        />

                        <Box className="layout-box">
                            <Box className="WrapperDetailActionClient">
                                <CompanyDetails
                                    clientName={clientName}
                                    companyEmail={clientEmail}
                                    phoneNumber={clientPhone}
                                />
                                <SolicitorDetails
                                    onSolicitorDetailUpdated={
                                        handleFormikDetailUpdated
                                    }
                                    validateAddCompany={
                                        handleFormikValidationForSolicitorDetailsOnAddCompanyButton
                                    }
                                    validateAddSolicitor={
                                        handleFormikValidationForSolicitorDetailsOnAddSolicitorButton
                                    }
                                />
                                <ClientSettings
                                    clientId={clientID}
                                    ChecklistStepData={clientChecklistStep}
                                    handleClick={handleClick}
                                    statusPage={statusPage}
                                    statusReplace={statusReplace}
                                    setStatusReplace={setStatusReplace}
                                    setFilename={setFileChecklistName}
                                    checkList={currentChecklist}
                                    checkListHistory={clientChecklist}
                                    InputcheckList={{
                                        filename: fileChecklistFilename,
                                        name: fileChecklistName,
                                    }}
                                    archiveDays={archiveDays}
                                    slaDays={slaDays}
                                    cm9Days={cm9Days}
                                    reworkDays={reworkDays}
                                />
                            </Box>
                        </Box>

                        {/* Box Confirmation Button */}
                        <Grid
                            container
                            mt={8}
                            spacing={3}
                            className={
                                matchesDownMD && statusPage === 'edit'
                                    ? 'FooterActionClient reverse-footer'
                                    : 'FooterActionClient'
                            }
                        >
                            {statusPage === 'edit' && !statusReplace ? (
                                <Grid
                                    item
                                    xs={12}
                                    sm={12}
                                    md={9}
                                    lg={7.5}
                                    xl={9}
                                >
                                    <ButtonComp
                                        className="action-button"
                                        type="warning"
                                        width="160px"
                                        onClick={() => setDialogRemove(true)}
                                        aria-haspopup="dialog"
                                    >
                                        Delete Client
                                    </ButtonComp>
                                </Grid>
                            ) : (
                                ''
                            )}

                            {!matches &&
                            !statusReplace &&
                            statusPage === 'edit' ? (
                                <>
                                    <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        md={3.5}
                                        lg={2.5}
                                        xl={1.5}
                                    >
                                        <ButtonComp
                                            className="action-button"
                                            type="neutral"
                                            width="160px"
                                            onClick={() =>
                                                setStatusPage('view')
                                            }
                                        >
                                            Cancel
                                        </ButtonComp>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        md={3}
                                        lg={2}
                                        xl={1.5}
                                    >
                                        <ButtonComp
                                            innerRef={innerButtonRef}
                                            className="action-button"
                                            type="primary"
                                            width="160px"
                                            isLoading={isSubmitting}
                                            loadingSize={22}
                                            disabled={isSubmitting}
                                            loadingText="PROCESSING"
                                            onClick={handleSubmitOnManageClient}
                                            testId="save-button-action-client"
                                            aria-haspopup={
                                                isHavingFile &&
                                                isHavingValidFile
                                                    ? 'dialog'
                                                    : 'false'
                                            }
                                        >
                                            Save
                                        </ButtonComp>
                                    </Grid>
                                </>
                            ) : (
                                <Grid
                                    item
                                    xs={12}
                                    sm={
                                        !statusReplace && statusPage !== 'add'
                                            ? 6
                                            : 12
                                    }
                                    md={
                                        !statusReplace && statusPage !== 'add'
                                            ? 3
                                            : 12
                                    }
                                    lg={
                                        !statusReplace && statusPage !== 'add'
                                            ? 4.5
                                            : 12
                                    }
                                    xl={
                                        !statusReplace && statusPage !== 'add'
                                            ? 3
                                            : 12
                                    }
                                >
                                    <Stack
                                        direction={
                                            matchesMD ||
                                            (matchesDownMD &&
                                                (statusReplace ||
                                                    statusPage === 'add'))
                                                ? 'column-reverse'
                                                : 'row'
                                        }
                                        spacing={4}
                                    >
                                        <ButtonComp
                                            className="action-button"
                                            type="neutral"
                                            width="160px"
                                            onClick={() =>
                                                setStatusPage('view')
                                            }
                                        >
                                            Cancel
                                        </ButtonComp>
                                        {statusPage === 'add' ? (
                                            <ButtonComp
                                                innerRef={innerButtonRef}
                                                className="action-button"
                                                type="primary"
                                                width="160px"
                                                isLoading={isSubmitting}
                                                loadingSize={22}
                                                disabled={isSubmitting}
                                                loadingText="PROCESSING"
                                                onClick={
                                                    handleSubmitOnManageClient
                                                }
                                                testid="save-button-action-client"
                                                aria-haspopup={
                                                    isHavingFile &&
                                                    isHavingValidFile
                                                        ? 'dialog'
                                                        : 'false'
                                                }
                                            >
                                                Save
                                            </ButtonComp>
                                        ) : (
                                            <ButtonComp
                                                innerRef={innerButtonRef}
                                                className="action-button"
                                                type="primary"
                                                width="160px"
                                                isLoading={
                                                    !statusReplace &&
                                                    isSubmitting
                                                }
                                                loadingSize={22}
                                                disabled={
                                                    !statusReplace &&
                                                    isSubmitting
                                                }
                                                loadingText="PROCESSING"
                                                onClick={
                                                    handleSubmitOnManageClient
                                                }
                                                testid="save-button-action-client"
                                                aria-haspopup={
                                                    isHavingFile &&
                                                    isHavingValidFile
                                                        ? 'dialog'
                                                        : 'false'
                                                }
                                            >
                                                Save
                                            </ButtonComp>
                                        )}
                                    </Stack>
                                </Grid>
                            )}
                        </Grid>
                    </ActionClientMeta.Provider>
                </Box>
            )}
        </Formik>
    );
};

export default MCActionContent;
